/**
 * Copyright ©2026 Drivepoint
 */

import React from "react";
import {Button} from "@mui/material";
import {flushSync} from "react-dom";
import Telemetry from "@services/telemetry/Telemetry";
import "./AuthenticationPageSwitch.css";

export type AuthenticationPageSwitchProps = {
  action: string;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  prompt: string;
  transitionDirection: "back" | "forward";
};

type AuthenticationViewTransition = {
  finished: Promise<void>;
};

type AuthenticationTransitionDocument = Document & {
  startViewTransition?: (callback: () => void) => AuthenticationViewTransition;
};

const TRANSITION_CLASS = "authentication-page-transition";
const BACK_TRANSITION_CLASS = "authentication-page-transition-back";
const FORWARD_TRANSITION_CLASS = "authentication-page-transition-forward";

function clearTransitionClasses(root: HTMLElement): void {
  root.classList.remove(TRANSITION_CLASS, BACK_TRANSITION_CLASS, FORWARD_TRANSITION_CLASS);
}

export default function AuthenticationPageSwitch(props: AuthenticationPageSwitchProps): any {
  function handleClick(event: React.MouseEvent<HTMLButtonElement>): void {
    Telemetry.track("AuthenticationPageSwitch Navigate", {action: props.action});
    const transitionDocument = document as AuthenticationTransitionDocument;
    const startViewTransition = transitionDocument.startViewTransition?.bind(transitionDocument);
    const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
    if (!startViewTransition || prefersReducedMotion) {
      props.onClick(event);
      return;
    }
    const root = document.documentElement;
    const directionClass = props.transitionDirection === "forward" ? FORWARD_TRANSITION_CLASS : BACK_TRANSITION_CLASS;
    let navigationStarted = false;
    root.classList.add(TRANSITION_CLASS, directionClass);
    try {
      const transition = startViewTransition(() => {
        navigationStarted = true;
        flushSync(() => props.onClick(event));
      });
      void transition.finished.catch(() => undefined).then(() => clearTransitionClasses(root));
    } catch (error: any) {
      clearTransitionClasses(root);
      if (!navigationStarted) {
        props.onClick(event);
        return;
      }
      throw error;
    }
  }

  return <div className="authentication-page-switch">
    <span className="authentication-page-switch-text">{props.prompt}</span>
    <Button className="authentication-page-switch-button" onClick={handleClick}>{props.action}</Button>
  </div>;
}
